/**
 * Sim Lab deck/transport phase machine (pure).
 *
 * Deck metaphor:
 * - Play  = start or resume the clock
 * - Pause = freeze clock in place (no rewind; Play resumes)
 * - Stop  = cue to scenario start (re-setup), leave paused ready
 * - Reset = reload scenario initials/pitch banks + Stop cue
 * - Complete = natural end → done (board frozen with reports)
 */

export type SimTransportPhase = "ready" | "playing" | "paused" | "done";

export type SimTransportEvent =
  | "cue"
  | "play"
  | "pause"
  | "stop"
  | "reset"
  | "complete";

export type SimTransportButtons = {
  play: boolean;
  pause: boolean;
  stop: boolean;
  reset: boolean;
  /** Scenario / mode / bank edits allowed. */
  edit: boolean;
};

/** Reduce transport phase. Invalid events keep the current phase. */
export function reduceSimTransport(
  phase: SimTransportPhase,
  event: SimTransportEvent,
): SimTransportPhase {
  switch (event) {
    case "cue":
    case "stop":
    case "reset":
      return "ready";
    case "play":
      if (phase === "ready" || phase === "paused" || phase === "done") {
        return "playing";
      }
      return phase;
    case "pause":
      if (phase === "playing") return "paused";
      return phase;
    case "complete":
      if (phase === "playing" || phase === "paused") return "done";
      return phase;
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

/** Which transport controls are enabled in this phase. */
export function simTransportButtons(
  phase: SimTransportPhase,
): SimTransportButtons {
  switch (phase) {
    case "ready":
      return {
        play: true,
        pause: false,
        stop: false,
        reset: true,
        edit: true,
      };
    case "playing":
      return {
        play: false,
        pause: true,
        stop: true,
        reset: true,
        edit: false,
      };
    case "paused":
      return {
        play: true,
        pause: false,
        stop: true,
        reset: true,
        edit: false,
      };
    case "done":
      return {
        play: true,
        pause: false,
        stop: true,
        reset: true,
        edit: true,
      };
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

/** True when the clock should advance (only playing). */
export function simTransportClockRunning(phase: SimTransportPhase): boolean {
  return phase === "playing";
}

/**
 * True when Play should re-prepare (cue) before ticking.
 * Mid-run pause resumes the same board; ready/done start fresh.
 */
export function simTransportPlayNeedsCue(phase: SimTransportPhase): boolean {
  return phase === "ready" || phase === "done";
}
